import { useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useTranslation } from "react-i18next";
import { formattedDate } from "../../../components/formatted/FormattedDateAndTime";
import { formattedColorMaterial } from "../../../components/formatted/FormattedText";
import { normalizeMaterialNameFromTranslation } from "../../../shared/utils/materialNameNormalizer";
import CampaignAnalysisChartCard from "./CampaignAnalysisChartCard";
import { CHART_HEIGHTS } from "./campaignChartConstants";
import { getCampaignAnalysisDayList } from "../campaignFormatUtils";
import { getChartTooltipOptions } from "../../../ui/components/tooltipTheme";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels,
);

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(0, 167, 166, ${alpha})`;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(0, 167, 166, ${alpha})`;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const VIEW_MODE_OPTIONS = [
  { value: "total", labelKey: "campaign.distributionViewTotal" },
  {
    value: "byMaterial",
    labelKey: "campaign.distributionViewByMaterial",
  },
];

const TIME_GRANULARITY_OPTIONS = [
  { value: "day", labelKey: "campaign.distributionPeriodDay" },
  { value: "week", labelKey: "campaign.distributionPeriodWeek" },
  { value: "month", labelKey: "campaign.distributionPeriodMonth" },
];

const startOfLocalDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const startOfWeekMonday = (date) => {
  const d = startOfLocalDay(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

const getBucketMeta = (date, granularity, t, language) => {
  const day = startOfLocalDay(date);

  if (granularity === "week") {
    const monday = startOfWeekMonday(day);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      key: `w-${monday.toISOString().slice(0, 10)}`,
      label: `${formattedDate(monday, t, true)} – ${formattedDate(sunday, t, true)}`,
      sortDate: monday,
    };
  }

  if (granularity === "month") {
    const monthStart = new Date(day.getFullYear(), day.getMonth(), 1);
    return {
      key: `m-${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, "0")}`,
      label: monthStart.toLocaleDateString(language || "fr-FR", {
        month: "short",
        year: "numeric",
      }),
      sortDate: monthStart,
    };
  }

  return {
    key: `d-${day.toISOString().slice(0, 10)}`,
    label: formattedDate(day, t, true),
    sortDate: day,
  };
};

const buildOrderedBuckets = (dayList, granularity, t, language) => {
  const buckets = [];
  const seen = new Set();

  dayList.forEach((day) => {
    const meta = getBucketMeta(day, granularity, t, language);
    if (seen.has(meta.key)) {
      return;
    }
    seen.add(meta.key);
    buckets.push(meta);
  });

  return buckets;
};

const CampaignDailyHistogram = ({
  comments,
  campaignPeriod,
  variant = "legacy",
}) => {
  const { t, i18n } = useTranslation();
  const [viewMode, setViewMode] = useState("total");
  const [timeGranularity, setTimeGranularity] = useState("day");
  const language = i18n.language || "fr-FR";

  const chartData = useMemo(() => {
    if (!campaignPeriod) {
      return { labels: [], datasets: [] };
    }

    const dayList = getCampaignAnalysisDayList(campaignPeriod);
    if (dayList.length === 0) {
      return { labels: [], datasets: [] };
    }

    const buckets = buildOrderedBuckets(dayList, timeGranularity, t, language);
    const labels = buckets.map((bucket) => bucket.label);
    const bucketKeys = buckets.map((bucket) => bucket.key);
    const filteredComments = (comments || []).filter(
      (comment) => comment.objectRemoved === true,
    );

    const resolveBucketKey = (date) =>
      getBucketMeta(date, timeGranularity, t, language).key;

    if (viewMode === "total") {
      const totals = Object.fromEntries(bucketKeys.map((key) => [key, 0]));

      filteredComments.forEach((comment) => {
        const date = new Date(comment.notificationDate || comment.time);
        const bucketKey = resolveBucketKey(date);
        if (!(bucketKey in totals)) {
          return;
        }
        const number = comment.number != null ? Number(comment.number) : 0;
        if (!Number.isNaN(number)) {
          totals[bucketKey] += number;
        }
      });

      return {
        labels,
        datasets: [
          {
            label: t("Objects removed"),
            data: bucketKeys.map((key) => totals[key] || 0),
            backgroundColor: "rgba(0, 167, 166, 0.6)",
            borderColor: "rgba(0, 167, 166, 1)",
            borderWidth: 1,
          },
        ],
      };
    }

    const byBucket = Object.fromEntries(bucketKeys.map((key) => [key, {}]));
    const allMaterials = new Set();

    filteredComments.forEach((comment) => {
      const date = new Date(comment.notificationDate || comment.time);
      const bucketKey = resolveBucketKey(date);
      if (!(bucketKey in byBucket)) {
        return;
      }
      const materialRaw = comment.material || t("Unknown");
      const material = normalizeMaterialNameFromTranslation(materialRaw);

      if (!byBucket[bucketKey][material]) {
        byBucket[bucketKey][material] = 0;
      }
      const number = comment.number != null ? Number(comment.number) : 0;
      if (!Number.isNaN(number)) {
        byBucket[bucketKey][material] += number;
      }
      allMaterials.add(material);
    });

    const materialArray = Array.from(allMaterials).sort();
    if (materialArray.length === 0) {
      return {
        labels,
        datasets: [
          {
            label: t("Objects removed"),
            data: bucketKeys.map(() => 0),
            backgroundColor: "rgba(0, 167, 166, 0.6)",
            borderColor: "rgba(0, 167, 166, 1)",
            borderWidth: 1,
          },
        ],
      };
    }

    const colors = materialArray.map((material) =>
      formattedColorMaterial(material),
    );

    return {
      labels,
      datasets: materialArray.map((material, index) => {
        const color = colors[index] || "#00A7A6";
        return {
          label: t(material),
          data: bucketKeys.map((key) => byBucket[key][material] || 0),
          backgroundColor: hexToRgba(color, 0.6),
          borderColor: hexToRgba(color, 1),
          borderWidth: 1,
        };
      }),
    };
  }, [comments, campaignPeriod, viewMode, timeGranularity, t, language]);

  const barWidth = timeGranularity === "day" ? 30 : 42;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: viewMode === "byMaterial",
        position: "top",
      },
      tooltip: {
        ...getChartTooltipOptions(),
        callbacks: {
          label: function (context) {
            return `${context.dataset.label || ""}: ${context.parsed.y}`;
          },
        },
      },
      datalabels: {
        anchor: "center",
        align: "center",
        formatter: (value) => {
          if (value && Number(value) >= 5) {
            return value.toString();
          }
          return "";
        },
        display: function (context) {
          const value = context.dataset.data[context.dataIndex];
          return value && Number(value) >= 5;
        },
        color: "#222222",
        font: {
          size: 12,
        },
      },
    },
    scales: {
      x: {
        stacked: viewMode === "byMaterial",
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
        barThickness: barWidth,
      },
      y: {
        stacked: viewMode === "byMaterial",
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
        title: {
          display: true,
          text: t("Number of objects removed"),
        },
      },
    },
  };

  const isDistribution = variant === "distribution";
  const chartHeight = isDistribution ? CHART_HEIGHTS.LARGE : "400px";

  const headerFilters = (
    <div className="flex flex-wrap items-center gap-3">
      <label className="campaign-analysis-card__select-wrap">
        <span className="campaign-analysis-card__select-label">
          {t("campaign.distributionPeriodLabel")}
        </span>
        <select
          className="campaign-analysis-card__select"
          value={timeGranularity}
          onChange={(event) => setTimeGranularity(event.target.value)}
        >
          {TIME_GRANULARITY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>
      </label>
      <label className="campaign-analysis-card__select-wrap">
        <span className="campaign-analysis-card__select-label">
          {t("campaign.distributionDisplayLabel")}
        </span>
        <select
          className="campaign-analysis-card__select"
          value={viewMode}
          onChange={(event) => setViewMode(event.target.value)}
        >
          {VIEW_MODE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  const chartBody = (
    <div
      className="overflow-x-auto campaign-analysis-card__chart"
      style={{ height: chartHeight }}
    >
      {chartData.labels.length > 0 ? (
        <div
          style={{
            minWidth: `${Math.max(chartData.labels.length * barWidth * 1.2, 400)}px`,
            height: "100%",
          }}
        >
          <Bar data={chartData} options={options} plugins={[ChartDataLabels]} />
        </div>
      ) : (
        <div className="campaign-analysis-card__empty">
          {t("No data available")}
        </div>
      )}
    </div>
  );

  if (isDistribution) {
    return (
      <CampaignAnalysisChartCard
        title={t("campaign.dailyRemovedObjectsTitle")}
        headerAction={headerFilters}
      >
        {chartBody}
      </CampaignAnalysisChartCard>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-[#d3d3d3] p-4">
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <h3 className="text-[18px] leading-[24px] font-semibold text-[#222222]">
          {t("Daily distribution of removed objects")}
        </h3>
        {headerFilters}
      </div>
      {chartBody}
    </div>
  );
};

export default CampaignDailyHistogram;
